import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import "./MyTechProfileCertificateComponent.css";
import AnimatedGradientText from "./AnimatedGradient";
import GitHubIcon from "@mui/icons-material/GitHub";
import CancelIcon from "@mui/icons-material/Cancel";
import { IconButton } from "@mui/material";
import { useSelector } from "react-redux";
import { pdfToPng } from "../../../utils/convertPdftoImage";

const MyTechProfileSolutionComponent = ({ certificateData }) => {
  return (
    <div className="mytechprofilesolution-container">
      {/* --- Title Section --- */}
      <div className="mytechprofilesolution-header">
        <h2 className="mytechprofilesolution-title">
          Project <AnimatedGradientText text={"Solutions"} />{" "}
        </h2>
        <p className="mytechprofilesolution-subtitle">
          A collection of my hands-on projects and solutions.
        </p>
      </div>

      {/* --- Cards Grid Section --- */}
      <div className="mytechprofilesolution-grid">
        {certificateData?.projectSolutions?.map((solution) => (
          <div key={solution.id} className="mytechprofilesolution-card">
            <div className="mytechprofilesolution-image-wrapper">
              <img
                src={solution?.img}
                alt={`${solution?.title}`}
                className="mytechprofilesolution-image"
              />
            </div>
            <div className="mytechprofilesolution-card-content">
              <h3 className="mytechprofilesolution-card-title">
                {solution?.title}
              </h3>
              <p className="mytechprofilesolution-card-details">
                {solution?.details}
              </p>
              <a
                href={solution?.githubLink}
                target="_blank"
                rel="noopener noreferrer"
                className="mytechprofilesolution-button"
              >
                <GitHubIcon className="mytechprofilesolution-button-icon" />
                View Solution
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MyTechProfileCertificateComponent = ({ certificationsRef }) => {
  const { profile } = useSelector((state) => state.editMyTechProfileStore);
  const [open, setOpen] = useState(false);
  const [selectedCert, setSelectedCert] = useState(null);

  const handleOpen = (cert) => {
    setSelectedCert(cert);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedCert(null);
  };

  const cardVariants = {
    hover: {
      scale: 1.05,
      transition: { duration: 0.3 },
    },
  };

  const iconVariants = {
    rest: { opacity: 0, y: 10 },
    hover: { opacity: 1, y: 0 },
  };

  const imageVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.1, transition: { duration: 0.4 } },
  };

  function getFileType(url) {
    const cleanUrl = url.split("?")[0].toLowerCase();

    if (cleanUrl.endsWith(".pdf")) return "pdf";
    if (cleanUrl.match(/\.(png|jpg|jpeg|webp|gif)$/)) return "image";

    return "unknown";
  }

  const [pdfPreviews, setPdfPreviews] = useState({});

  useEffect(() => {
    profile?.certificates?.forEach(async (cert) => {
      try {
        const preview = await pdfToPng(cert.image);
        setPdfPreviews((prev) => ({
          ...prev,
          [cert.id]: preview,
        }));
      } catch (err) {
        console.error("PDF preview failed", err);
      }
    });
  }, [profile?.certificates]);

  return (
    <div className="mytechprofilecertification-container">
      {/* --- Title Section --- */}
      <div className="mytechprofilecertification-header">
        <p
          ref={certificationsRef}
          className="mytechprofilecertification-subtitle"
        >
          I am a certified Developer
        </p>
        <h2 className="mytechprofilecertification-title">
          My <AnimatedGradientText text="Certificates" />
        </h2>
      </div>

      {/* --- Cards Section --- */}
      <div className="mytechprofilecertification-grid">
        {profile?.certificates?.map((cert) => (
          <motion.div
            key={cert.id}
            className={`mytechprofilecertification-card ${
              cert.certificateIssuedByAcetechies ? "acetechies-certified" : ""
            }`}
            onClick={() => handleOpen(cert)}
            variants={cardVariants}
            initial="rest"
            whileHover="hover"
            animate="rest"
          >
            <div className="mytechprofilecertification-image-wrapper">
              {/* --- Conditionally render the chip --- */}
              {cert.certificateIssuedByAcetechies && (
                <span className="mytechprofilecertification-card__chip">
                  AceTechies Certified
                </span>
              )}
              <motion.div className="mytechprofilecertification-shine" />
              <motion.img
                src={
                  getFileType(cert?.image) === "pdf"
                    ? pdfPreviews[cert.id]
                    : cert?.image
                }
                alt={`${cert.title} certificate`}
                className="mytechprofilecertification-image"
                // variants={imageVariants}
              />
            </div>
            <div className="mytechprofilecertification-card-content">
              <h3 className="mytechprofilecertification-card-title">
                {cert.title}
              </h3>
              <motion.div
                className="mytechprofilecertification-icon-wrapper"
                variants={iconVariants}
              >
                <ArrowOutwardIcon />
              </motion.div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* --- Modal Section --- */}
      {selectedCert && (
        <Modal
          open={open}
          onClose={handleClose}
          aria-labelledby="certificate-modal-title"
          aria-describedby="certificate-modal-description"
          className="mytechprofilecertification-modal-container"
        >
          <Box className="mytechprofilecertification-modal-box">
            <IconButton onClick={handleClose}>
              <CancelIcon
                sx={{ color: "#fff", fontSize: "28px", cursor: "pointer" }}
              />
            </IconButton>

            <img
              src={
                getFileType(selectedCert?.image) === "pdf"
                  ? pdfPreviews[selectedCert.id]
                  : selectedCert?.image
              }
              alt={selectedCert?.title}
              className="mytechprofilecertification-modal-image"
            />

            <div className="mytechprofilecertification-modal-details">
              <h4 className="mytechprofilecertification-modal-section-title">
                Tech Stack
              </h4>
              <div className="mytechprofilecertification-tech-stack">
                {selectedCert?.techStack?.map((tech) => (
                  <div
                    key={tech?.name}
                    className="mytechprofilecertification-tech-item"
                  >
                    <img
                      src={tech?.icon}
                      alt={tech?.name}
                      className="mytechprofilecertification-tech-logo"
                    />
                    <span>{tech?.name}</span>
                  </div>
                ))}
              </div>

              <h3
                id="certificate-modal-title"
                className="mytechprofilecertification-modal-title"
              >
                {selectedCert?.title}
              </h3>
              <p
                id="certificate-modal-description"
                className="mytechprofilecertification-modal-description"
              >
                {selectedCert?.details}
              </p>
            </div>
            {selectedCert?.projectSolutions && (
              <MyTechProfileSolutionComponent certificateData={selectedCert} />
            )}
          </Box>
        </Modal>
      )}
    </div>
  );
};

export default MyTechProfileCertificateComponent;
